import { merge } from 'lodash-es';
import { isValidElement, type PropsWithChildren, type ReactNode } from 'react';

import type { SingleValueSlots } from './iterate-slots.js';
import {
  defaultXAxisConfig,
  defaultYAxisConfig,
} from '../../core/components/sparkline-display/constants.js';
import type { SparklineDisplayProps } from '../../core/components/sparkline-display/SparklineDisplay.js';
import { defaultSingleValueConfig } from '../defaults.js';
import type {
  SingleValueConfigProperties,
  SingleValueProps,
  SingleValueSparklineConfig,
} from '../types/single-value.js';

function getNodeInnerText(node: ReactNode): string {
  // String literals
  if (typeof node === 'string') {
    return node;
  }

  // Empty
  if (
    node === null ||
    typeof node === 'boolean' ||
    typeof node === 'undefined'
  ) {
    return '';
  }

  // Numeric children
  if (typeof node === 'number') {
    return node.toString();
  }

  // Children prop
  if (
    isValidElement<PropsWithChildren>(node) &&
    node.props &&
    node.props.children
  ) {
    return getNodeInnerText(node.props.children);
  }

  // Default
  return '';
}

/**
 * Deserializes a SingleValue string config
 *
 * @param serializedConfig - string
 * @returns SingleValueConfigProperties
 */
export function deserializeConfig(
  serializedConfig: string,
): SingleValueConfigProperties {
  try {
    return merge({}, defaultSingleValueConfig, JSON.parse(serializedConfig));
  } catch {
    return defaultSingleValueConfig;
  }
}

/**
 * Checks if the contextConfig is a serialized-string configuration
 * @param contextConfig - context configuration.
 * @returns true if the received contextConfig is a string.
 */
export function isSerializedConfig(
  contextConfig: SingleValueConfigProperties | string,
): contextConfig is string {
  return typeof contextConfig === 'string';
}

/**
 * Normalizes a SingleValue config to look like the configuration object
 * expected by the SingleValueConfig.
 *
 * @param config - config from context
 * @returns SingleValueConfigProperties
 */
export function normalizeConfig(
  config: SingleValueConfigProperties,
): SingleValueConfigProperties {
  return {
    ...config,
    label: getNodeInnerText(config.label),
  };
}

export const buildSparklineConfig = (
  slotsSparklineConfig?: SparklineDisplayProps,
  contextSparklineConfig?: SingleValueSparklineConfig,
): SingleValueSparklineConfig => {
  const contextThresholds = contextSparklineConfig?.thresholds ?? [];
  const slotThresholds = slotsSparklineConfig?.thresholds ?? [];

  return {
    thresholds: contextThresholds.concat(slotThresholds),
    color: slotsSparklineConfig?.color ?? contextSparklineConfig?.color,
    variant: slotsSparklineConfig?.variant ?? contextSparklineConfig?.variant,
    xAxis:
      slotsSparklineConfig?.xAxis ??
      contextSparklineConfig?.xAxis ??
      defaultXAxisConfig,
    yAxis:
      slotsSparklineConfig?.yAxis ??
      contextSparklineConfig?.yAxis ??
      defaultYAxisConfig,
    showTicks:
      slotsSparklineConfig?.showTicks ?? contextSparklineConfig?.showTicks,
    showContextValues:
      slotsSparklineConfig?.showContextValues ??
      contextSparklineConfig?.showContextValues,
    gapPolicy:
      slotsSparklineConfig?.gapPolicy ?? contextSparklineConfig?.gapPolicy,
    curve: slotsSparklineConfig?.curve,
  };
};

/**
 * Build SingleValue configuration.
 *
 * @param props - config from props
 * @param slots - config from slot components
 * @param contextConfig - config from the context
 * @returns SingleValueConfigProperties
 */
export function buildSingleValueConfig(
  props: Omit<SingleValueProps, 'data' | 'prefixIcon'>,
  slots: SingleValueSlots,
  contextConfig: SingleValueConfigProperties,
) {
  const config = {
    label: props.label ?? contextConfig.label,
    color: props.color ?? contextConfig.color ?? defaultSingleValueConfig.color,
    unit: props.unit ?? contextConfig.unit ?? defaultSingleValueConfig.unit,
    formatter: props.formatter ?? contextConfig.formatter,
    thresholds: props.thresholds ?? contextConfig.thresholds,
    alignment:
      props.alignment ??
      contextConfig.alignment ??
      defaultSingleValueConfig.alignment,
    sparkline: undefined as SingleValueSparklineConfig | undefined,
  };

  if (slots.sparkline || contextConfig.sparkline) {
    config.sparkline = buildSparklineConfig(
      slots.sparkline,
      contextConfig.sparkline,
    );
  }

  return config;
}
